import {
  isShownOnlyKind,
  OUTSIDE_GOAL_LABELS,
  type OutsideGoalStepKind,
  type ShownOnlyKind,
} from "../../../skills/leadcue-reply/scripts/lib/outside-goal.ts";

/*
 * A reply outside the campaign's goal (`docs/2026-09-29-outside-goal-replies.md`, "The rep-facing
 * state"): the writer is not a prospect, so the app wrote no draft and asks nothing. The sheet says
 * what it is in one line, shows the writer's own sentence, and leaves the rep three ways on: reply
 * from the box, mark it handled where no email is needed, or, for a misfire on a real lead, draft
 * anyway. No suggested reply and no second question to confirm: "without heavy assistance" (Dawid).
 */
export function OutsideGoalSheet(props: {
  kind: OutsideGoalStepKind;
  quote: string | null;
  /** The step's own instructions, read only for a reply the host shows and never drafts. */
  detail?: string | null;
  busy: boolean;
  onReply: () => void;
  onHandled: () => void;
  onDraftAnyway: () => void;
}) {
  if (isShownOnlyKind(props.kind)) return <ShownOnlySheet {...props} kind={props.kind} />;
  const nothing = props.kind === "nothing_to_reply_to";
  return (
    <article className="lcr-sheet lcr-outside-goal" aria-labelledby="lcr-outside-goal-heading">
      <h3 id="lcr-outside-goal-heading">
        {nothing ? "Nothing to reply to" : "Not a sales reply"} · handle it yourself
      </h3>
      <p>
        {/* The heading already says "Nothing to reply to"; the kind is named only where it adds. */}
        {nothing ? null : (
          <>
            <strong>{OUTSIDE_GOAL_LABELS[props.kind]}.</strong>{" "}
          </>
        )}
        {props.quote ? (
          <>
            They wrote: <q>{props.quote}</q>
          </>
        ) : (
          "The message has no words to answer."
        )}
      </p>
      <p className="lcr-meta-text">
        {nothing
          ? "No draft was written."
          : "No draft was written: the writer is not a prospect for this campaign."}
      </p>
      <div className="lcr-actions">
        <button type="button" className="lcr-primary" disabled={props.busy} onClick={props.onReply}>
          Write a reply <kbd className="lcr-kbd">R</kbd>
        </button>
        <button type="button" disabled={props.busy} onClick={props.onHandled}>
          Handled outside email
        </button>
      </div>
      {!nothing && (
        <button
          type="button"
          className="lcr-linklike"
          disabled={props.busy}
          onClick={props.onDraftAnyway}
        >
          This is a sales reply: draft anyway
        </button>
      )}
    </article>
  );
}

/** Why nothing was drafted, per kind the host shows and never drafts. */
const SHOWN_ONLY_REASON: Record<ShownOnlyKind, string> = {
  crosses_campaigns:
    "No draft was written: the other campaign's offer and facts may not apply to this reply.",
  not_addressed: "No draft was written: nothing shows the lead wrote this to you.",
};

/*
 * A reply the host shows and never drafts, with no run behind it: Bison filed it under a different
 * campaign than the rest of its conversation (docs/2026-10-07-failures-nobody-sees.md), or under one
 * of the seller's mailboxes without it being addressed to that mailbox
 * (docs/2026-10-07-reply-attribution.md). Never offered "Draft anyway": there is nothing to draft
 * from. One click settles it either way, by email or without.
 */
function ShownOnlySheet(props: {
  kind: ShownOnlyKind;
  detail?: string | null;
  busy: boolean;
  onReply: () => void;
  onHandled: () => void;
}) {
  return (
    <article className="lcr-sheet lcr-outside-goal" aria-labelledby="lcr-outside-goal-heading">
      <h3 id="lcr-outside-goal-heading">{OUTSIDE_GOAL_LABELS[props.kind]} · handle it yourself</h3>
      {props.detail ? <p>{props.detail}</p> : null}
      <p className="lcr-meta-text">{SHOWN_ONLY_REASON[props.kind]}</p>
      <div className="lcr-actions">
        <button type="button" className="lcr-primary" disabled={props.busy} onClick={props.onReply}>
          Write a reply <kbd className="lcr-kbd">R</kbd>
        </button>
        <button type="button" disabled={props.busy} onClick={props.onHandled}>
          Handled outside email
        </button>
      </div>
    </article>
  );
}

/** Why "Draft anyway" was refused, in words the rep can act on; the route names the code. */
export function draftAnywayRefusal(code: string | null): string {
  switch (code) {
    case "run_in_flight":
      return "A draft for this reply is already being prepared.";
    case "plan_changed":
    case "current_turn_event_not_reopenable":
      return "The lead wrote again, so this reply can no longer be drafted. Answer the newest message.";
    case "current_turn_sent":
      return "A reply to this message was already sent.";
    case "current_turn_answered":
      return "Someone already answered this message in Bison, so the app will not draft it.";
    case "suppressed":
      return "This lead asked not to be contacted.";
    case "not_outside_goal":
      return "This reply is no longer waiting for you to handle it.";
    case "crosses_campaigns":
      return "Bison filed this reply under another campaign, so the app cannot draft it. Answer it yourself.";
    case "not_addressed":
      return "This reply was not addressed to your mailbox, so the app cannot draft it. Answer it yourself.";
    case "draft_anyway_unavailable":
      return "Drafting is not running here, so a draft cannot be asked for.";
    default:
      return `The app could not draft this reply (${code ?? "error"}).`;
  }
}
